import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Info, Lightbulb } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { GLOSSARY } from "@/lib/glossary";
import { toast } from "sonner";
import { api, errMsg, zmw } from "@/lib/api";
import { Label } from "@/components/ui/label";

export function useLoad(path, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    if (!path) return;
    try {
      const { data } = await api.get(path);
      setData(data);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  useEffect(() => { load(); }, [load]);
  return { data, error, reload: load, setData };
}

export async function act(fn, ok) {
  try {
    const r = await fn();
    if (ok) toast.success(ok);
    return r?.data ?? true;
  } catch (e) {
    toast.error(errMsg(e));
    return null;
  }
}

const TONES = {
  green: "bg-emerald-50 text-emerald-800 ring-emerald-600/20",
  amber: "bg-amber-50 text-amber-800 ring-amber-600/25",
  red: "bg-rose-50 text-rose-800 ring-rose-600/20",
  blue: "bg-sky-50 text-sky-800 ring-sky-600/20",
  gray: "bg-stone-100 text-stone-700 ring-stone-500/20",
};
const STATUS_TONE = {
  confirmed: "green", approved: "green", paid: "green", active: "green", verified: "green", repaid: "green", reconciled: "green", posted: "green",
  "pending verification": "amber", submitted: "amber", "under review": "amber", requested: "amber", processing: "amber", pending: "amber",
  funding: "blue", assessment: "blue", application: "blue", proposed: "blue", pending_activation: "amber", pending_verification: "amber", pending_approval: "amber",
  "more information required": "amber", more_info: "amber", "held for return": "amber", draft: "gray", returned: "gray", cancelled: "gray", declined: "gray",
  rejected: "red", defaulted: "red", recovery: "red", "written off": "red", overdue: "red", suspended: "red", removed: "red", protected: "blue", unverified: "gray",
};

export function StatusBadge({ status, tone, testid }) {
  const t = tone || STATUS_TONE[String(status).toLowerCase()] || "gray";
  return (
    <span data-testid={testid || `status-${String(status).toLowerCase().replace(/\s+/g, "-")}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset whitespace-nowrap ${TONES[t]}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {String(status).replace(/_/g, " ")}
    </span>
  );
}

export function Tip({ k, text, light }) {
  const [open, setOpen] = useState(false);
  const e = k && GLOSSARY[k];
  if (!e && !text) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" aria-label="What does this mean?" data-testid={`tip-${k || "info"}`} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
          onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); setOpen(true); }}
          className={`inline-grid h-4 w-4 shrink-0 place-items-center rounded-full align-middle normal-case transition-colors ${light ? "bg-white/20 text-white hover:bg-white/30" : "bg-sky-100 text-sky-700 hover:bg-sky-200"}`}>
          <Info className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 text-sm normal-case tracking-normal" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} data-testid="tip-content">
        {e ? <><div className="font-semibold text-brand">{e[0]}</div><p className="mt-1 leading-snug text-stone-700">{e[2]}</p><p className="mt-2 text-xs text-stone-500">Official term: <b>{e[1]}</b></p></> : <p className="leading-snug text-stone-700">{text}</p>}
      </PopoverContent>
    </Popover>
  );
}

export function PageHeader({ overline, title, children, sub, explain }) {
  return (
    <div className="mb-8 space-y-4 fade-up">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          {overline && <div className="overline mb-2">{overline}</div>}
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-brand" data-testid="page-title">{title}</h1>
          {sub && <p className="mt-2 max-w-2xl text-sm sm:text-base text-stone-600">{sub}</p>}
        </div>
        {children && <div className="flex flex-wrap gap-2">{children}</div>}
      </div>
      {explain && (
        <div className="flex gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-950" data-testid="page-explainer">
          <Lightbulb className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
          <div><b>What this page shows: </b>{explain}</div>
        </div>
      )}
    </div>
  );
}

export function Stat({ label, value, hint, testid, tone = "default", big, tip, to }) {
  const tones = { default: "bg-white", gold: "bg-accent-soft", dark: "bg-brand text-white", red: "bg-rose-50" };
  const body = (
    <div className={`h-full rounded-xl border border-stone-200/80 p-5 ${tones[tone]} lift ${to ? "cursor-pointer ring-sky-400 transition-shadow hover:ring-2" : ""}`} data-testid={testid}>
      <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${tone === "dark" ? "text-amber-200" : "text-stone-500"}`}>{label}{tip && <Tip k={GLOSSARY[tip] ? tip : null} text={GLOSSARY[tip] ? null : tip} light={tone === "dark"} />}</div>
      <div className={`mt-2 font-mono font-bold tracking-tight ${big ? "text-3xl sm:text-4xl" : "text-xl sm:text-2xl"} ${tone === "dark" ? "text-white" : "text-brand"}`}>
        {typeof value === "number" ? zmw(value) : value}
      </div>
      {hint && <div className={`mt-1.5 text-xs ${tone === "dark" ? "text-sky-100/90" : "text-stone-500"}`}>{hint}</div>}
      {to && <div className="mt-2 text-xs font-semibold text-[#0369a1]">Open and take action →</div>}
    </div>
  );
  return to ? <Link to={to} className="block" data-testid={testid ? `${testid}-link` : undefined}>{body}</Link> : body;
}

export function Card({ title, children, action, className = "", testid, tip }) {
  return (
    <section className={`rounded-xl border border-stone-200/80 bg-white ${className}`} data-testid={testid}>
      {title && (
        <div className="flex items-center justify-between gap-3 border-b border-stone-100 px-5 py-4">
          <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-brand">{title}{tip && <Tip k={GLOSSARY[tip] ? tip : null} text={GLOSSARY[tip] ? null : tip} />}</h2>
          {action}
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function Loading({ error }) {
  if (error) return <div className="rounded-lg bg-rose-50 p-4 text-rose-800" data-testid="load-error">{error}</div>;
  return <div className="flex items-center gap-2 p-8 text-stone-500" data-testid="loading"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;
}

export function Empty({ children }) {
  return <div className="rounded-lg border border-dashed border-stone-300 p-8 text-center text-sm text-stone-500">{children}</div>;
}

export function Note({ children, tone = "info" }) {
  const c = tone === "warn" ? "bg-amber-50 text-amber-900 border-amber-200" : tone === "red" ? "bg-rose-50 text-rose-900 border-rose-200" : "bg-sky-50/70 text-sky-950 border-sky-100";
  return <div className={`flex gap-2.5 rounded-lg border p-3.5 text-sm ${c}`}><Info className="mt-0.5 h-4 w-4 shrink-0" /><div>{children}</div></div>;
}

export function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-stone-800">{label}</Label>
      {children}
      {hint && <p className="text-xs text-stone-500">{hint}</p>}
    </div>
  );
}

export function Table({ cols, rows, empty = "Nothing here yet.", testid }) {
  if (!rows?.length) return <Empty>{empty}</Empty>;
  return (
    <div className="-mx-5 overflow-x-auto" data-testid={testid}>
      <table className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="border-b border-stone-200 text-left text-xs uppercase tracking-wider text-stone-500">
            {cols.map((c) => <th key={c.k} className={`px-5 py-2.5 font-semibold ${c.right ? "text-right" : ""}`}><span className="inline-flex items-center gap-1">{c.h}{c.tip && <Tip k={GLOSSARY[c.tip] ? c.tip : null} text={GLOSSARY[c.tip] ? null : c.tip} />}</span></th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-b border-stone-100 hover:bg-stone-50/70 transition-colors" data-testid={`row-${r.id || i}`}>
              {cols.map((c) => <td key={c.k} className={`px-5 py-3 align-top ${c.right ? "text-right font-mono" : ""}`}>{c.r ? c.r(r) : r[c.k]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
